import {
  useState, useRef, useCallback, useMemo,
} from 'react';
import { message } from 'antd';
import type { QuestionnaireQuestion } from '../types/questionnaire';
import { CARD_TRANSITION_DURATION_MS } from '../data/constants';
import { BRAND_PRIMARY } from '../tokens/colors';
import { hasRequiredFollowUp, isQuestionAnswered } from '../utils/question-answered';

interface FlatQuestion extends QuestionnaireQuestion {
  secIdx: number
  secTitle: string
  secColor: string
  secInstructions?: string
  localIdx: number
  localTotal: number
}

interface UseQuestionnaireNavigationParams {
  flatQuestions: FlatQuestion[]
  answers: Record<string, any>
  onComplete: (meta: any, answers: Record<string, any>) => void
  onBack: () => void
  template: { templateId?: string; templateVersion?: number } | null
  initialQuestionIndex?: number
}

const useQuestionnaireNavigation = ({
  flatQuestions,
  answers,
  onComplete,
  onBack,
  template,
  initialQuestionIndex,
}: UseQuestionnaireNavigationParams) => {
  const [questionIndex, setQuestionIndex] = useState(initialQuestionIndex ?? 0);
  const [isVisible, setIsVisible] = useState(true);
  const cardRef = useRef<HTMLDivElement>(null);
  const answersRef = useRef(answers);
  answersRef.current = answers;

  // Prevents concurrent transitions from stacking setQuestionIndex calls
  // which would skip questions when the user clicks rapidly.
  const isTransitioning = useRef(false);

  const totalQuestions = flatQuestions.length;
  const currentQuestion = flatQuestions[questionIndex] || null;
  const sectionColor = currentQuestion?.secColor || BRAND_PRIMARY;

  const animateTransition = useCallback((callback: () => void) => {
    if (isTransitioning.current) return;
    isTransitioning.current = true;
    setIsVisible(false);
    setTimeout(() => {
      callback();
      setIsVisible(true);
      if (cardRef.current) cardRef.current.scrollTop = 0;
      isTransitioning.current = false;
    }, CARD_TRANSITION_DURATION_MS);
  }, []);

  const handleGoNext = useCallback(() => {
    if (isTransitioning.current) return;

    if (questionIndex < totalQuestions - 1) {
      animateTransition(() => setQuestionIndex((i) => i + 1));
    } else {
      const currentAnswers = answersRef.current;
      const unansweredIndex = flatQuestions.findIndex(
        (flatQuestion) => flatQuestion.required
          && !isQuestionAnswered(flatQuestion, currentAnswers),
      );
      if (unansweredIndex !== -1) {
        message.warning(
          'Please answer all required questions before finishing.',
        );
        animateTransition(() => setQuestionIndex(unansweredIndex));
        return;
      }
      onComplete(
        {
          templateId: template?.templateId,
          templateVersion: template?.templateVersion,
        },
        currentAnswers,
      );
    }
  }, [
    questionIndex,
    totalQuestions,
    flatQuestions,
    animateTransition,
    onComplete,
    template,
  ]);

  const handleGoPrev = useCallback(() => {
    if (isTransitioning.current) return;

    if (questionIndex > 0) {
      animateTransition(() => setQuestionIndex((i) => i - 1));
    } else {
      onBack();
    }
  }, [questionIndex, animateTransition, onBack]);

  const isManualAdvanceType = currentQuestion
    && (
      currentQuestion.type === 'dual-period'
      || currentQuestion.type === 'checkbox'
      || currentQuestion.type === 'text'
      || currentQuestion.type === 'frequency-with-followup'
      || (currentQuestion.type === 'radio' && hasRequiredFollowUp(currentQuestion))
    );

  const isCurrentAnswered = currentQuestion
    && (!currentQuestion.required || isQuestionAnswered(currentQuestion, answers));

  const questionHint = useMemo(() => {
    if (!currentQuestion) return null;
    switch (currentQuestion.type) {
      case 'dual-period':
        return 'Answer Yes/No — examples appear if Yes';
      case 'radio':
        return hasRequiredFollowUp(currentQuestion)
          ? 'Select one'
          : 'Select one — auto-advances';
      case 'frequency':
        return 'Select one — auto-advances';
      case 'frequency-with-followup':
        return 'Select frequency — auto-advances';
      case 'checkbox':
        return 'Select all that apply';
      default:
        return null;
    }
  }, [currentQuestion]);

  return {
    questionIndex,
    totalQuestions,
    currentQuestion,
    sectionColor,
    isVisible,
    cardRef,
    isManualAdvanceType,
    isCurrentAnswered,
    questionHint,
    handleGoNext,
    handleGoPrev,
  };
};

export { isQuestionAnswered };
export default useQuestionnaireNavigation;
